/**
 * Object utilities — comparison, type checking, and manipulation.
 *
 * Canonical implementations consolidated from:
 * - packages/ssm-web5/src/utils/objects.js
 * - apps/ssm-web4/src/app/utils/objects/index.js
 * - apps/ssm-admin/src/utils/objects.js
 */

/**
 * Check whether a value is a plain object (not an array, null, etc.).
 * @param {*} obj
 * @returns {boolean}
 */
export const isObject = (obj) => obj && typeof obj === 'object' && obj.constructor === Object;

/**
 * Return a shallow copy of `obj` with all falsy-valued own properties removed.
 *
 * API decision: returns a **new** object (web4 behaviour) instead of mutating
 * in place (web5/admin behaviour). Callers that discarded the return value are
 * unaffected; callers that relied on mutation should assign the result.
 *
 * @param {Object} obj
 * @returns {Object} A new object without falsy values
 */
export const cleanEmptyValues = (obj) => {
    if (!obj) return {};
    const result = {};
    Object.keys(obj).forEach((key) => {
        if (obj[key]) {
            result[key] = obj[key];
        }
    });
    return result;
};

/**
 * Deeply compare two objects or arrays for structural equality.
 *
 * Supports an optional `clearEmptyFields` flag (web4 superset) which strips
 * falsy properties via `cleanEmptyValues` before comparing.
 *
 * @param {*} objA
 * @param {*} objB
 * @param {boolean} [clearEmptyFields=false]
 * @returns {boolean}
 */
export const isEqual = (objA, objB, clearEmptyFields) => {
    if (clearEmptyFields) {
        objA = cleanEmptyValues(objA);
        objB = cleanEmptyValues(objB);
    }

    const type = Object.prototype.toString.call(objA);
    if (type !== Object.prototype.toString.call(objB)) return false;

    // Only compare objects and arrays
    if (['[object Array]', '[object Object]'].indexOf(type) < 0) return false;

    const objALen = type === '[object Array]' ? objA.length : Object.keys(objA).length;
    const objBLen = type === '[object Array]' ? objB.length : Object.keys(objB).length;
    if (objALen !== objBLen) return false;

    const compare = (item1, item2) => {
        const itemType = Object.prototype.toString.call(item1);

        if (['[object Array]', '[object Object]'].indexOf(itemType) >= 0) {
            if (!isEqual(item1, item2)) return false;
        } else {
            if (itemType !== Object.prototype.toString.call(item2)) return false;

            if (itemType === '[object Function]') {
                if (item1.toString() !== item2.toString()) return false;
            } else {
                if (item1 !== item2) return false;
            }
        }
    };

    if (type === '[object Array]') {
        for (let i = 0; i < objALen; i++) {
            if (compare(objA[i], objB[i]) === false) return false;
        }
    } else {
        for (const key in objA) {
            if (objA.hasOwnProperty(key)) {
                if (compare(objA[key], objB[key]) === false) return false;
            }
        }
    }

    return true;
};

/**
 * Shallow comparison of two objects, treating functions as equal.
 *
 * @param {Object} objA
 * @param {Object} objB
 * @returns {boolean}
 */
export function shallowEqual(objA, objB) {
    if (objA === objB) return true;
    const aKeys = Object.keys(objA);
    const bKeys = Object.keys(objB);
    const len = aKeys.length;

    if (bKeys.length !== len) return false;

    for (let i = 0; i < len; i++) {
        const key = aKeys[i];
        const a = objA[key];
        const b = objB[key];
        if (a !== b && !(typeof a === 'function' && typeof b === 'function')) {
            return false;
        }
    }

    return true;
}

/**
 * Check whether a value is "empty" — null, undefined, empty object, empty
 * array, or a non-object non-number.
 *
 * @param {*} obj
 * @returns {boolean}
 */
export const isEmptyObject = (obj) => {
    if (obj == null) return true;
    if (obj.length > 0) return false;
    if (obj.length === 0) return true;
    if (typeof obj === 'number' && !isNaN(obj)) return false;
    if (typeof obj !== 'object') return true;

    for (const key in obj) {
        if (obj.hasOwnProperty(key)) return false;
    }

    return true;
};
